<script lang="ts" setup>
import { SancareToggle } from '@sancare/typescript-commons'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import AdvancedSearchCriteriaGroups from '@/filters/AdvancedSearchCriteriaGroups.vue'
import { hasPermission, RoleEnum, savedSearchPermissions } from '@/misc/permissions/permissionsHelper'
import { doesSearchIdExist, saveCurrentSavedSearch } from '@/saved-searches/helpers'
import ShowActiveSearchesModal from '@/saved-searches/ShowActiveSearchesModal.vue'
import { DEFAULT_SEARCH_ID } from '@/saved-searches/types'
import { canShareWithSancare, canShareWithTeam, savedSearchLabels } from '@/saved-searches/utils'
import StayListCriteriaParameters from '@/stay-list/StayListCriteriaParameters.vue'
import { StayListCriteriaProps } from '@/stay-list/types'
import { useStore } from '@/store'
import { PrivacyEnum } from '@/store/modules/settings/types'
import type { Search } from '@/store/modules/stay-saved-search/types'

const props = defineProps<StayListCriteriaProps>()

const emit = defineEmits<{
  'set-quick-mode': [enabled: boolean]
}>()

const store = useStore()
const router = useRouter()

const searchLabels = computed(() => savedSearchLabels(props.mode))

// State
const requestError = ref<string | null>(null)
const requestSuccess = ref<string | null>(null)

const params = computed(() => store.state.stayList.stayListParams)
const currentSearch = computed(() => store.state.stayList.currentSearch)

// Requests
const addSavedSearchRequest = computed(() => store.state.savedSearches.addSavedSearchRequest)
const editSavedSearchRequest = computed(() => store.state.savedSearches.editSavedSearchRequest)
const fetchSavedSearchesRequest = computed(() => store.state.savedSearches.fetchSavedSearchesRequest)

// Search state
const isSearchCreation = computed(() => store.state.stayList.stayListParams.searchCreation)
const isSearchEdition = computed(() => doesSearchIdExist(store.state.stayList.stayListParams.searchEdition))

// User permissions
const isAdmin = computed(() => store.state.login.userRole === 'ROLE_SANCARE_DEV')
const isSearchEditor = computed(() =>
  hasPermission(RoleEnum.ROLE_SANCARE_SEARCH_MANAGER, store.state.login.userRole)
)

// Current state
const currentSearchEdited = computed(() => store.state.stayList.currentSearchEdited)
const currentUser = computed(() => store.state.login.currentUser)
const currentUsername = computed(() => store.state.login.username)
const editionInProgress = computed(() => store.state.stayList.editionInProgress)
const shortHandStayListParams = computed(() => store.getters.shortHandStayListParams)

const activeSearches = computed(() => {
  let searchIds = store.state.stayList.activeSearches
  const searchesList = store.state.savedSearches.savedSearches

  searchIds = Array.isArray(searchIds) ? searchIds : [searchIds]
  searchIds = searchIds.map((searchId: number | string) =>
    typeof searchId === 'number' ? searchId : parseInt(searchId, 10)
  )

  return searchesList.filter((search) => searchIds.includes(search.id))
})

const isSancareUser = computed(() => store.state.login.currentUser.sancareUser)
const activeSearchIds = computed(() => activeSearches.value.map((s) => s.id))

const isOwner = computed(() =>
  currentSearch.value.user === null
    || currentSearch.value.user.username === currentUsername.value
)

const canShowSancareSearchOption = computed(() =>
  (currentSearch.value.privacy === 'sancareSearch' || isAdmin.value)
    && canShareWithSancare(currentSearch.value.type)
)

const isRefreshUrlNeeded = computed(() =>
  (currentSearch.value.criteriaGroups?.length > 0)
    || (currentSearch.value.globalCriteriaGroup?.criteriaList?.length > 0)
)

watch(addSavedSearchRequest, (req) => {
  if (req.error) {
    requestSuccess.value = null
    requestError.value = req.error
  } else if (req.ok) {
    requestError.value = null
    requestSuccess.value = 'Recherche enregistrée'
  }
})

watch(editSavedSearchRequest, (req) => {
  if (req.error) {
    requestSuccess.value = null
    requestError.value = req.error
  } else if (req.ok) {
    requestError.value = null
    requestSuccess.value = 'Modifications enregistrées'
  }
})

onMounted(() => {
  store.dispatch('initStayList')
})

function setParameter(type: string, value: string | number): void {
  store.commit('setStayListParam', { type, value })
  onEdit()
  store.dispatch('refreshStayList', { resetStaySelection: type !== 'pageSize' })
  store.dispatch('refreshStayListUrl', {})
}

function setSearchName(name: string): void {
  store.commit('setSearchName', name)
  onEdit()
}

function setSearchPrivacy(privacy: string): void {
  store.commit('setSearchPrivacy', privacy)
  onEdit()
}

function setSearchCategory(category: string): void {
  if (!userCanEditSearchCategory()) {
    return
  }
  store.commit('setSearchCategory', category)
  onEdit()
}

function handleSearchNameChange(event: Event): void {
  const target = event.target as HTMLInputElement
  setSearchName(target.value)
}

function handleSearchPrivacyChange(event: Event): void {
  const target = event.target as HTMLSelectElement
  setSearchPrivacy(target.value)
}

function handleSearchCategoryChange(event: Event): void {
  const target = event.target as HTMLInputElement
  setSearchCategory(target.value)
}

function onEdit(): void {
  requestError.value = null
  requestSuccess.value = null
}

function clearMessage(): void {
  requestSuccess.value = null
  requestError.value = null
}

function clearCriteria(): void {
  store.commit('clearCurrentSearchCriteria')
  requestError.value = null

  store.dispatch('refreshStayList', { resetStaySelection: true })
  if (!isRefreshUrlNeeded.value) {
    return
  }
  store.dispatch('refreshStayListUrl', {})
}

function resetEdition(): void {
  onEdit()
  store.dispatch('resetEdition', {})
}

function loadSavedSearch(search: Search | null): void {
  if (search) {
    store.commit('loadSavedSearch', search)
  } else {
    store.commit('newSavedSearch')
  }
  setParameter('searchEdition', DEFAULT_SEARCH_ID)
}

function editSelectedSearch(search: Search): void {
  store.commit('loadSavedSearch', search)
  const stayListQuery = {
    ...shortHandStayListParams.value,
    edit: search.id,
    searchId: undefined
  }
  const newRoute = {
    name: 'staylist',
    query: stayListQuery,
    params: { pmsiType: props.pmsiType },
  }

  store.commit('setStayListParam', { type: 'searchEdition', value: search.id })
  router.push(newRoute)
}

async function saveSavedSearch(): Promise<void> {
  if (!isSearchCreation.value && !isSearchEdition.value) {
    requestError.value = 'Aucune recherche en cours d\'édition'
    return
  }

  if (!currentSearch.value.name || currentSearch.value.name === '') {
    requestError.value = 'Nom de la recherche invalide'
    return
  }

  if (!currentSearch.value.privacy || currentSearch.value.privacy === '') {
    requestError.value = 'Niveau de partage incorrect'
    return
  }

  if (!currentSearch.value.savedSearchCategory.name || currentSearch.value.savedSearchCategory.name === '') {
    requestError.value = 'Nom de la catégorie invalide'
    return
  }

  await saveCurrentSavedSearch()

  store.commit('setInitialCriteriaToCurrentSearch')
  store.commit('loadSavedSearch', currentSearch.value)
  store.commit('setEditionInProgress', false)
}

function canDisplayActiveSearchesProperties(): boolean {
  return activeSearches.value.length > 0 && !isSearchEdition.value
}

function getSearchNameOrCategory(): string {
  if (activeSearches.value.length === 1) {
    return activeSearches.value[0].name
  }
  if (activeSearches.value.length > 0) {
    return activeSearches.value[0].savedSearchCategory.name
  }
  return ''
}

function userCanEditSearchCategory(): boolean {
  return (
    isSearchEditor.value
      || isOwner.value
      || currentSearch.value.privacy === PrivacyEnum.PUBLIC_SEARCH
  )
}

function switchOperators(): void {
  clearMessage()
  store.commit(
    'updateOperators',
    currentSearch.value.searchOperators === 'orAnd' ? 'andOr' : 'orAnd'
  )
  store.dispatch('refreshStayList', { resetStaySelection: true })
}
</script>

<template>
  <div
    id="advanced-criteria-builder"
    class="stay-list-criteria card"
    :class="{ 'opposite-mode': params.improveRecallMode === true }"
  >
    <div class="card-body">
      <h2
        v-if="params.improveRecallMode === true"
        class="recall-optimisation-mode-title"
      >
        Mode optimisation du rappel
      </h2>
      <div
        v-if="canDisplayActiveSearchesProperties() || isQuickModeAvailable"
        class="d-flex align-items-center"
      >
        <div v-if="canDisplayActiveSearchesProperties()">
          {{ getSearchNameOrCategory() }}
        </div>
        <div
          v-if="isQuickModeAvailable"
          class="ms-auto d-flex align-items-center"
        >
          <label
            for="quick-mode-toggle"
            class="me-2 text-secondary"
          >
            <small>Aperçu rapide</small>
          </label>
          <sancare-toggle
            id="quick-mode-toggle"
            :value="isQuickModeEnabled"
            @toggle-change="(enabled) => emit('set-quick-mode', enabled)"
          />
        </div>
      </div>
      <stay-list-criteria-parameters
        :mode="mode"
        @edit="onEdit"
        @set-parameter="setParameter"
      />
      <hr v-if="isSearchCreation || isSearchEdition">
      <div v-if="isSearchCreation || isSearchEdition">
        <h2 class="mb-2">
          {{ isSearchCreation ? 'Création' : 'Modification' }} d'une
          {{ searchLabels.singular }} avancée
        </h2>
        <div
          v-if="fetchSavedSearchesRequest.fetching"
          class="loader loader-center"
        />
        <template v-else>
          <div class="d-flex justify-content-between gap-2">
            <div class="flex-sm-grow-1 col">
              Nom
            </div>
            <div class="flex-sm-grow-1 col">
              Partage
            </div>
            <div
              v-if="PrivacyEnum.PRIVATE_SEARCH !== currentSearch.privacy"
              class="flex-sm-grow-1 col"
            >
              Catégorie
            </div>
          </div>
          <div class="my-1 d-flex justify-content-between gap-4">
            <div class="flex-sm-grow-1 col">
              <input
                :value="currentSearch.name"
                type="text"
                class="form-control"
                @change="handleSearchNameChange"
              >
            </div>
            <div class="flex-sm-grow-1 col">
              <select
                :value="currentSearch.privacy"
                :disabled="!savedSearchPermissions.canEditPrivacy(currentSearch, currentUser)"
                class="form-select"
                @change="handleSearchPrivacyChange"
              >
                <option value="privateSearch">
                  Privée
                </option>
                <option
                  v-if="canShareWithTeam(mode) && isSancareUser"
                  value="teamSearch"
                >
                  Equipe
                </option>
                <option value="publicSearch">
                  Publique
                </option>
                <option
                  v-if="currentSearch.privacy === PrivacyEnum.SANCARE_SEARCH || isAdmin"
                  :disabled="!isAdmin"
                  value="sancareSearch"
                >
                  Sancare
                </option>
              </select>
            </div>
            <div
              v-if="PrivacyEnum.PRIVATE_SEARCH !== currentSearch.privacy"
              class="flex-sm-grow-1 col"
            >
              <input
                :value="currentSearch.savedSearchCategory.name"
                :disabled="!userCanEditSearchCategory()"
                type="text"
                class="form-control"
                @change="handleSearchCategoryChange"
              >
            </div>
          </div>
        </template>
      </div>
      <hr>
      <div>
        <h2 class="mb-2">
          Conditions
        </h2>
        <sancare-toggle
          class="pt-2 pb-4 align-items-center"
          :label="'Inverser les opérateurs &quot;ET&quot;/&quot;OU&quot; dans les conditions'"
          :label-position="'right'"
          :value="currentSearch.searchOperators === 'orAnd'"
          :disabled="fetchSavedSearchesRequest.fetching"
          @toggle-change="switchOperators"
        />
        <advanced-search-criteria-groups
          :disabled="fetchSavedSearchesRequest.fetching"
          :mode="mode"
          :pmsi-type="pmsiType"
        />
      </div>
      <div class="row g-0 align-items-center">
        <div class="col">
          <div
            v-if="requestError"
            class="text-danger"
          >
            {{ requestError }}
          </div>
          <div
            v-else-if="requestSuccess"
            class="text-success"
          >
            {{ requestSuccess }}
          </div>
        </div>
        <div class="col-auto">
          <button
            v-if="editionInProgress && (isSearchEdition || isSearchCreation)"
            :disabled="!editionInProgress || !currentSearch.name || !currentSearch.privacy"
            class="btn btn-link"
            @click="saveSavedSearch"
          >
            Enregistrer la {{ searchLabels.singular }}
          </button>
        </div>
        <div class="col-auto">
          <button
            v-if="currentSearchEdited && (isSearchCreation || isSearchEdition)"
            class="btn btn-link"
            @click="resetEdition"
          >
            Annuler les modifications
          </button>
          <button
            v-else-if="currentSearch.criteriaGroups.length || currentSearch.globalCriteriaGroup.criteriaList.length"
            class="btn btn-link"
            @click="clearCriteria"
          >
            Retirer les critères
          </button>
        </div>
        <div
          v-if="activeSearches.length"
          class="col-auto"
        >
          <show-active-searches-modal
            :active-search-ids="activeSearchIds"
            :pmsi-type="pmsiType"
            :mode="mode"
            @load-saved-search="(s) => editSelectedSearch(s)"
          >
            <template #button>
              <button class="btn btn-link">
                {{ (activeSearches.length > 1) ? 'Recherches sélectionnées' : 'Recherche sélectionnée' }}
              </button>
            </template>
          </show-active-searches-modal>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
h2 {
  font-size: 1.1rem;
}
</style>
